import React, { useState } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import {
  add, alertCircle, create, trash, documentText, handLeft,
  checkmarkCircle,
} from 'ionicons/icons';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useFamilyVisits } from '@/hooks/useFamilies';
import { useFamilyNeeds, useMemberNeeds } from '@/hooks/useNeeds';
import { useCreateVisit, useDeleteVisit, useVisits } from '@/hooks/useVisits';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/store/authStore';
import type { Visit } from '@/types/visit';
import type { Need } from '@/types/need';
import {
  VISIT_TYPE_LABELS,
  NEED_STATUS_LABELS, NEED_STATUS_COLORS,
  NEED_CATEGORY_LABELS, NEED_CATEGORY_COLORS,
  NEED_PRIORITY_LABELS, NEED_PRIORITY_COLORS,
} from '@/utils/labels';
import VisitModal from './VisitModal';
import NeedModal from './NeedModal';
import NeedDetailModal from './NeedDetailModal';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

interface VisitsNeedsTabsProps {
  familyId?: string;
  memberId?: string;
  context: 'family' | 'member';
}

const VISIT_TYPE_COLORS: Record<string, string> = {
  pastorala: '#4f46e5',
  sectoriala: '#0891b2',
  nevoie_speciala: '#dc2626',
  incurajare: '#059669',
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'd MMM yyyy', { locale: ro }); } catch { return iso; }
};

const Badge: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <span style={{
    display: 'inline-flex', padding: '2px 8px', borderRadius: 5,
    fontSize: 10, fontWeight: 600, color, background: `${color}15`,
  }}>
    {text}
  </span>
);

const VisitsNeedsTabs: React.FC<VisitsNeedsTabsProps> = ({ familyId, memberId, context }) => {
  const [activeTab, setActiveTab] = useState<'vizite' | 'nevoi'>('vizite');
  const { can } = usePermission();
  const canEditVisits = can('visits.edit');
  const canDeleteVisits = can('visits.delete');
  const canCreateVisits = can('visits.create');
  const canCreateNeeds = can('needs.create');
  const [presentToast] = useIonToast();
  const user = useAuthStore((s) => s.user);

  // Data hooks — prefer family visits, fallback to member-level visits
  const memberVisitsEnabled = !familyId && !!memberId;
  const { data: familyVisits } = useFamilyVisits(familyId);
  const { data: memberVisitsData } = useVisits(
    memberVisitsEnabled ? { member_id: memberId!, per_page: 50 } : undefined,
    memberVisitsEnabled,
  );
  const visits = familyId ? familyVisits : (memberVisitsData?.data ?? []);
  const { data: familyNeedsData } = useFamilyNeeds(context === 'family' ? familyId : undefined);
  const { data: memberNeedsData } = useMemberNeeds(context === 'member' ? memberId : undefined);
  const deleteVisitMut = useDeleteVisit();
  const createVisitMut = useCreateVisit();

  const needsList = context === 'family'
    ? (familyNeedsData?.data ?? [])
    : (memberNeedsData?.data ?? []);

  // Visit modals
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [deleteVisitId, setDeleteVisitId] = useState<string | null>(null);

  // Need modals
  const [showNeedModal, setShowNeedModal] = useState(false);
  const [editingNeed, setEditingNeed] = useState<Need | null>(null);
  const [detailNeedId, setDetailNeedId] = useState<string | null>(null);

  const handleEditVisit = (visit: Visit) => {
    setEditingVisit(visit);
    setShowVisitModal(true);
  };

  const handleDeleteVisit = async () => {
    if (!deleteVisitId) return;
    await deleteVisitMut.mutateAsync(deleteVisitId);
    setDeleteVisitId(null);
  };

  const handleQuickVisit = async () => {
    if (!familyId && !memberId) return;
    try {
      await createVisitMut.mutateAsync({
        family_id: familyId || undefined,
        member_id: memberId || undefined,
        visit_date: new Date().toISOString().slice(0, 10),
        visit_type: 'pastorala',
        notes: null,
        has_special_need: false,
        special_need_type: null,
        special_need_notes: null,
      });
      presentToast({ message: 'Vizită înregistrată!', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      presentToast({ message: 'Eroare la înregistrarea vizitei.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  };

  // Check if recently visited (within last 30 days)
  const recentlyVisited = (() => {
    const list = Array.isArray(visits) ? visits : (visits as any)?.data ?? [];
    if (list.length === 0) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return list.some((v: any) => new Date(v.visit_date) >= thirtyDaysAgo);
  })();

  const visitList = Array.isArray(visits) ? visits : (visits as any)?.data ?? [];

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 24px',
      marginBottom: 20, border: '1px solid #ececf2',
    }}>
      {/* Tab header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className={`form-chip${activeTab === 'vizite' ? ' form-chip--active' : ''}`}
            onClick={() => setActiveTab('vizite')}
            style={{ fontSize: 13 }}
          >
            Vizite {visitList.length > 0 && `(${visitList.length})`}
          </button>
          <button
            className={`form-chip${activeTab === 'nevoi' ? ' form-chip--active' : ''}`}
            onClick={() => setActiveTab('nevoi')}
            style={{ fontSize: 13 }}
          >
            Nevoi {needsList.length > 0 && `(${needsList.length})`}
          </button>
        </div>
        {activeTab === 'vizite' && canCreateVisits && (familyId || memberId) && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {recentlyVisited && (
              <span style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginRight: 4 }}>
                <IonIcon icon={checkmarkCircle} style={{ fontSize: 13, verticalAlign: 'middle' }} /> Vizitat recent
              </span>
            )}
            <button
              className="form-btn form-btn--small form-btn--primary"
              onClick={handleQuickVisit}
              disabled={createVisitMut.isPending}
              style={{ padding: '4px 10px', fontSize: 12 }}
              title={`Vizitat de ${user?.name ?? 'mine'} — azi`}
            >
              <IonIcon icon={checkmarkCircle} style={{ fontSize: 14 }} />
              {createVisitMut.isPending ? '...' : ' Vizitat'}
            </button>
            {familyId && (
              <button
                className="form-btn form-btn--small"
                onClick={() => { setEditingVisit(null); setShowVisitModal(true); }}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <IonIcon icon={add} style={{ fontSize: 14 }} /> Vizită
              </button>
            )}
          </div>
        )}
        {activeTab === 'nevoi' && canCreateNeeds && (
          <button
            className="form-btn form-btn--small"
            onClick={() => { setEditingNeed(null); setShowNeedModal(true); }}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            <IonIcon icon={add} style={{ fontSize: 14 }} /> Nevoie
          </button>
        )}
      </div>

      {/* Visits Tab */}
      {activeTab === 'vizite' && (
        <>
          {visitList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 16px', color: '#94a3b8', fontSize: 13 }}>
              <IonIcon icon={documentText} style={{ fontSize: 28, display: 'block', marginBottom: 6, color: '#ececf2' }} />
              Nicio vizită înregistrată.
            </div>
          ) : (
            visitList.map((visit: any) => {
              const typeColor = VISIT_TYPE_COLORS[visit.visit_type] ?? '#64748b';
              return (
                <div key={visit.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Badge text={VISIT_TYPE_LABELS[visit.visit_type as keyof typeof VISIT_TYPE_LABELS] ?? visit.visit_type} color={typeColor} />
                      <span style={{ fontSize: 12, color: '#8181a5' }}>
                        {fmtDate(visit.visit_date)}
                      </span>
                    </div>
                    {familyId && (canEditVisits || canDeleteVisits) && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {canEditVisits && (
                          <button
                            onClick={() => handleEditVisit(visit)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                          >
                            <IonIcon icon={create} style={{ fontSize: 14, color: '#94a3b8' }} />
                          </button>
                        )}
                        {canDeleteVisits && (
                          <button
                            onClick={() => setDeleteVisitId(visit.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                          >
                            <IonIcon icon={trash} style={{ fontSize: 14, color: '#94a3b8' }} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: '#8181a5', margin: '4px 0 0' }}>
                    {visit.visited_by_name ?? visit.visitor?.name ?? '—'}
                    {visit.notes && ` — ${visit.notes}`}
                  </p>
                  {visit.has_special_need && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                      <IonIcon icon={alertCircle} style={{ fontSize: 12, color: '#dc2626' }} />
                      <span style={{ fontSize: 11, color: '#dc2626' }}>
                        {visit.special_need_type || 'Nevoie specială'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {/* Needs Tab */}
      {activeTab === 'nevoi' && (
        <>
          {needsList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 16px', color: '#94a3b8', fontSize: 13 }}>
              <IonIcon icon={handLeft} style={{ fontSize: 28, display: 'block', marginBottom: 6, color: '#ececf2' }} />
              Nicio nevoie înregistrată.
            </div>
          ) : (
            needsList.map((need: Need) => {
              const catColor = NEED_CATEGORY_COLORS[need.category] ?? '#64748b';
              const statusColor = NEED_STATUS_COLORS[need.status] ?? '#64748b';
              const prioColor = NEED_PRIORITY_COLORS[need.priority] ?? '#475569';
              return (
                <div
                  key={need.id}
                  onClick={() => setDetailNeedId(need.id)}
                  style={{
                    padding: '10px 0', borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#1c1d21' }}>
                      {need.title}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {fmtDate(need.created_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <Badge text={NEED_STATUS_LABELS[need.status]} color={statusColor} />
                    <Badge text={NEED_CATEGORY_LABELS[need.category]} color={catColor} />
                    {need.priority !== 'normala' && (
                      <Badge text={NEED_PRIORITY_LABELS[need.priority]} color={prioColor} />
                    )}
                  </div>
                  {need.description && (
                    <p style={{ fontSize: 12, color: '#8181a5', margin: '4px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {need.description}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {/* Visit Modal */}
      <VisitModal
        isOpen={showVisitModal}
        onClose={() => { setShowVisitModal(false); setEditingVisit(null); }}
        visit={editingVisit}
        familyId={familyId}
      />

      {/* Need Modal */}
      <NeedModal
        isOpen={showNeedModal}
        onClose={() => { setShowNeedModal(false); setEditingNeed(null); }}
        need={editingNeed}
        familyId={familyId}
        memberId={memberId}
      />

      {/* Need Detail */}
      <NeedDetailModal
        isOpen={!!detailNeedId}
        onClose={() => setDetailNeedId(null)}
        needId={detailNeedId}
        onEdit={(need) => { setEditingNeed(need); setShowNeedModal(true); }}
      />

      {/* Delete Visit Confirm */}
      <ConfirmDialog
        isOpen={!!deleteVisitId}
        title="Ștergere Vizită"
        message="Ești sigur că vrei să ștergi această vizită?"
        confirmLabel="Șterge"
        variant="danger"
        isLoading={deleteVisitMut.isPending}
        onConfirm={async () => {
          await handleDeleteVisit();
          presentToast({ message: 'Vizită ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
        }}
        onCancel={() => setDeleteVisitId(null)}
      />
    </div>
  );
};

export default VisitsNeedsTabs;
